import { useEffect, useRef, useState } from 'react'
import type { Game, Member } from '../../types/game'
import { ResourceIcon } from '../shared/ResourceIcon'
import { PlayerMarker } from '../shared/PlayerMarker'
import { assetUrl } from '../../utils/assets'

interface ColonyCollapseModalProps {
  game: Game
  player: Game['players'][number]
  members: Member[]
  onSubmitOffer: (offers: Array<{ resource: string; amount: number }>) => void
  onResolve: (force?: boolean) => void
}


function titleCase(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export function ColonyCollapseModal({ game, player, members, onSubmitOffer, onResolve }: ColonyCollapseModalProps) {
  const event = game.event_handler.current_event
  const [amounts, setAmounts] = useState<Record<string, number>>({})
  const eventDuration = game.event_handler.event_duration_seconds
  const [secondsLeft, setSecondsLeft] = useState(eventDuration)
  const hasTriggeredResolveRef = useRef(false)

  useEffect(() => {
    setSecondsLeft(eventDuration)
    hasTriggeredResolveRef.current = false
  }, [event?.id])

  useEffect(() => {
    if (!event) {
      setAmounts({})
      return
    }

    const offerableResources = [
      ...Object.entries(event.requirements)
        .filter(([, amount]) => amount > 0)
        .map(([resource]) => resource),
      'propolis',
    ]
    const nextAmounts = Object.fromEntries(
      offerableResources.map((resource) => [resource, player.offered_resources[resource as keyof typeof player.offered_resources] ?? 0])
    )
    setAmounts(nextAmounts)
  }, [event, player.offered_resources])

  useEffect(() => {
    if (!event) {
      return
    }
    const intervalId = window.setInterval(() => {
      setSecondsLeft((current) => Math.max(0, current - 1))
    }, 1000)
    return () => window.clearInterval(intervalId)
  }, [event])

  if (!event) return null

  const severity = game.event_handler.current_severity
  const severityClass = severity && severity !== 'base' ? ` severity-${severity}` : ''
  const requirements = Object.entries(event.requirements).filter(([, amount]) => amount > 0)
  const allPlayersOffered = game.players.every((player) => Object.keys(player.offered_resources).length > 0)
  const timeLabel = `${String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:${String(secondsLeft % 60).padStart(2, '0')}`
  const aliasByColor = Object.fromEntries(members.filter((member) => member.color).map((member) => [member.color as string, member.alias]))

  const getPlayerMax = (resource: string) => {
    if (resource === 'egg') {
      return player.n_eggs
    }
    if (resource === 'worker') {
      return player.max_workers_to_offer
    }
    return player.resources[resource as keyof typeof player.resources] ?? 0
  }

  // propolis is never required, but it can always be offered: it jokers for
  // any missing resource
  const showPropolisOffer = getPlayerMax('propolis') > 0 || (amounts['propolis'] ?? 0) > 0

  const handleSubmitOffer = () => {
    const offers = [...requirements.map(([resource]) => resource), 'propolis']
      .map((resource) => ({ resource, amount: amounts[resource] ?? 0 }))
    onSubmitOffer(offers)
  }

  // green: this offer covers what the other players' offers still leave open;
  // orange: more than the remaining need. With hidden offers, others' amounts
  // must not influence the outline — that would leak how much they offered —
  // so the whole demand is the reference instead.
  const offerStatusClass = (resource: string, required: number) => {
    const amount = amounts[resource] ?? 0
    const offeredByOthers = game.are_offers_public
      ? game.players
          .filter((p) => p.color !== player.color)
          .reduce(
            (sum, p) => sum + (p.offered_resources[resource as keyof typeof p.offered_resources] ?? 0),
            0
          )
      : 0
    const remaining = Math.max(0, required - offeredByOthers)
    if (amount > remaining) return ' offer-surplus'
    if (amount === remaining && required > 0) return ' offer-met'
    return ''
  }

  const renderPlayerOffers = (playerColor: string, offeredResources: Record<string, number>) => {
    const hasPlacedOffer = Object.keys(offeredResources).length > 0
    const entries = Object.entries(offeredResources).filter(([, amount]) => amount > 0)
    if (!hasPlacedOffer) {
      return 'has not placed an offer yet'
    }
    if (entries.length === 0) {
      return game.are_offers_public ? 'offered nothing' : 'placed an offer'
    }
    if (!game.are_offers_public) {
      return 'placed an offer'
    }
    return entries.map(([resource, amount]) => (
      <div key={`${playerColor}-${resource}`} style={{ display: 'contents' }}>
        <div>{amount}</div>
        <ResourceIcon resource={resource as any} />
      </div>
    ))
  }

  useEffect(() => {
    if (hasTriggeredResolveRef.current) {
      return
    }
    if (allPlayersOffered) {
      hasTriggeredResolveRef.current = true
      onResolve(false)
      return
    }

    if (secondsLeft === 0) {
      hasTriggeredResolveRef.current = true
      onResolve(true)
    }
  }, [allPlayersOffered, onResolve, secondsLeft, hasTriggeredResolveRef])

  return (
    <div className="colony-collapse-event-modal-root">
      <div className="colony-collapse-event-overlay" />
      <div className="colony-collapse-event-container">
        <div className="colony-collapse-event-clock">
          <div className="colony-collapse-event-clock-content">{timeLabel}</div>
        </div>
        <div className={`colony-collapse-event tableau${severityClass}`}>
          <div className="colony-collapse-event-content desktop-shell-view">
            <div className="colony-collapse-event-column">
              <div className="colony-collapse-event-header">
                <div className="colony-collapse-event-title">{event.name}</div>
                <div className="colony-collapse-event-description">{event.description}</div>
              </div>
              <img src={assetUrl('images', 'events', `${event.code}.png`)} alt="" />
            </div>
            <div className="colony-collapse-event-column">
              <div className="colony-collapse-event-body-container">
                <div className="colony-collapse-event-body-header">Collect</div>
                <div className="colony-collapse-event-body-content">
                  {requirements.map(([resource, amount]) => (
                    <div key={`requirement-${resource}`} className="colony-collapse-event-requirement">
                      <div>{amount}</div>
                      <ResourceIcon resource={resource as any} />
                    </div>
                  ))}
                </div>
                <div className="colony-collapse-event-body-header">On Failure</div>
                <div className="colony-collapse-event-body-content">{event.effect}</div>
                <hr />
                <div id="colony-collapse-event-player-slots">
                  {game.players.map((player) => (
                    <div key={player.color} className="colony-collapse-event-player-slot">
                      <div className="colony-collapse-event-player-slot-header">
                        <PlayerMarker color={player.color} className="opponent-marker" />
                        <div className="colony-collapse-event-player-name">{aliasByColor[player.color] ?? player.color}</div>
                      </div>
                      <div className={`colony-collapse-event-player-offer-status${Object.keys(player.offered_resources).length === 0 ? ' to-offer' : ''}`}>
                        {renderPlayerOffers(player.color, player.offered_resources as Record<string, number>)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="colony-collapse-event-player-offer">
                  <div className="colony-collapse-event-offer-title">Your Offer:</div>
                  {requirements.map(([resource, required]) => (
                    <div key={`offer-${resource}`} className="colony-collapse-event-player-offer-resource">
                      <input
                        className={`input${offerStatusClass(resource, required)}`}
                        value={amounts[resource] ?? 0}
                        type="number"
                        min={0}
                        max={getPlayerMax(resource)}
                        onChange={(event_) =>
                          setAmounts((prev) => ({
                            ...prev,
                            [resource]: Math.min(getPlayerMax(resource), Math.max(0, parseInt(event_.target.value, 10) || 0)),
                          }))
                        }
                      />
                      <ResourceIcon resource={resource as any} />
                    </div>
                  ))}
                  {showPropolisOffer && (
                    <div
                      className="colony-collapse-event-player-offer-resource"
                      title="Propolis counts as any missing resource"
                    >
                      <input
                        className="input"
                        value={amounts['propolis'] ?? 0}
                        type="number"
                        min={0}
                        max={getPlayerMax('propolis')}
                        onChange={(event_) =>
                          setAmounts((prev) => ({
                            ...prev,
                            propolis: Math.min(getPlayerMax('propolis'), Math.max(0, parseInt(event_.target.value, 10) || 0)),
                          }))
                        }
                      />
                      <ResourceIcon resource="propolis" />
                    </div>
                  )}
                  <div className="colony-collapse-event-player-offer-submit" onClick={handleSubmitOffer}>
                    Submit
                  </div>
                </div>
                {showPropolisOffer && (
                  <div className="colony-collapse-event-offer-hint">Propolis counts as any missing resource.</div>
                )}
              </div>
            </div>
          </div>
          <div className="colony-collapse-event-mobile-shell mobile-shell-view">
            <div className="mobile-modal-shell colony-collapse-event-mobile-card">
                <div className="mobile-modal-header">
                  <div className="mobile-modal-title">{event.name}</div>
                </div>
              <div className="mobile-modal-hero colony-collapse-event-mobile-hero">
                <img src={assetUrl('images', 'events', `${event.code}.png`)} alt="" />
              </div>
              <div className="mobile-modal-section">
                <div className="mobile-modal-section-title">Collect Before The Timer Ends</div>
                <div className="colony-collapse-event-mobile-requirements">
                  {requirements.map(([resource, amount]) => (
                    <div key={`mobile-requirement-${resource}`} className="colony-collapse-event-requirement">
                      <div>{amount}</div>
                      <ResourceIcon resource={resource as any} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="mobile-modal-section">
                <div className="mobile-modal-section-title">On Failure</div>
                <div className="colony-collapse-event-mobile-effect">{event.effect}</div>
              </div>
              <div className="mobile-modal-section colony-collapse-event-mobile-players-section">
                <div className="mobile-modal-section-title">Colony Offers</div>
                <div className="colony-collapse-event-mobile-player-list">
                  {game.players.map((player) => (
                    <div key={`mobile-${player.color}`} className="colony-collapse-event-player-slot">
                      <div className="colony-collapse-event-player-slot-header">
                        <PlayerMarker color={player.color} className="opponent-marker" />
                        <div className="colony-collapse-event-player-name">{aliasByColor[player.color] ?? player.color}</div>
                      </div>
                      <div className={`colony-collapse-event-player-offer-status${Object.keys(player.offered_resources).length === 0 ? ' to-offer' : ''}`}>
                        {renderPlayerOffers(player.color, player.offered_resources as Record<string, number>)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mobile-modal-section">
                <div className="mobile-modal-section-title">Your Offer</div>
                <div className="mobile-resource-input-grid">
                  {requirements.map(([resource, required]) => (
                    <div key={`mobile-offer-${resource}`} className="mobile-resource-input-card">
                      <div className="mobile-resource-input-label">{titleCase(resource)}</div>
                      <div className="mobile-resource-input-control">
                        <ResourceIcon resource={resource as any} />
                        <input
                          className={`input${offerStatusClass(resource, required)}`}
                          value={amounts[resource] ?? 0}
                          type="number"
                          min={0}
                          max={getPlayerMax(resource)}
                          onChange={(event_) =>
                            setAmounts((prev) => ({
                              ...prev,
                              [resource]: Math.min(getPlayerMax(resource), Math.max(0, parseInt(event_.target.value, 10) || 0)),
                            }))
                          }
                        />
                      </div>
                    </div>
                  ))}
                  {showPropolisOffer && (
                    <div className="mobile-resource-input-card">
                      <div className="mobile-resource-input-label">Propolis</div>
                      <div className="mobile-resource-input-control">
                        <ResourceIcon resource="propolis" />
                        <input
                          className="input"
                          value={amounts['propolis'] ?? 0}
                          type="number"
                          min={0}
                          max={getPlayerMax('propolis')}
                          onChange={(event_) =>
                            setAmounts((prev) => ({
                              ...prev,
                              propolis: Math.min(getPlayerMax('propolis'), Math.max(0, parseInt(event_.target.value, 10) || 0)),
                            }))
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
                {showPropolisOffer && (
                  <div className="colony-collapse-event-offer-hint">Propolis counts as any missing resource.</div>
                )}
              </div>
              <div className="mobile-modal-footer">
                <div className="colony-collapse-event-player-offer-submit lobby-start" onClick={handleSubmitOffer}>
                  Submit
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
